import { useMemo, useState } from "react";
import { Box, Heading, Tooltip, Icon, HStack, Text } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import FilterForm from "../../components/FilterForm";
import Loader from "../../components/loader";
import TicketsPerService from "../../components/organization/reports/TicketsPerService";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import useReportRenderSettingLoader from "../../hooks/useReportRenderSettingLoader";
import {
  DEFAULT_RENDER_CONFIG,
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  getRenderConfigSummaryItems,
  normalizeRenderConfig,
} from "../../store/renderConfig";
import { saveReportRenderSetting } from "../../store/actions/defaultAction";
import { downloadTicketsPerServiceCsvFiles } from "../../store/ticketsPerServiceExport";

const TicketsPerServicePage = ({ showFilterForm = true, showTitle = 1 }) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.authReducer);
  const {
    loading,
    reportRange,
    reportsData,
    organizationId,
    savingReportRenderSetting,
  } = useSelector((state) => state.defaultReducer);
  const [renderConfig, setRenderConfig] = useState(DEFAULT_RENDER_CONFIG);

  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_organization_tickets_per_service
      : REPORT_TYPE_IDS.monthly_organization_tickets_per_service;

  const report = reportsData.find(
    (item) => item.reportTypeId === reportTypeId && item.organizationId === organizationId
  );
  const data = report?.data || [];
  const renderConfigOptions = useMemo(
    () => ({ columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS }),
    []
  );
  const isLoadingRenderSetting = useReportRenderSettingLoader({
    userId: userData?.id,
    organizationId,
    reportTypeId,
    renderConfigOptions,
    setRenderConfig,
  });
  const effectiveRenderConfig = useMemo(
    () => normalizeRenderConfig(renderConfig, renderConfigOptions),
    [renderConfig, renderConfigOptions]
  );
  const presentationSummaryItems = useMemo(
    () => getRenderConfigSummaryItems(effectiveRenderConfig, renderConfigOptions),
    [effectiveRenderConfig, renderConfigOptions]
  );
  const csvScopes = useMemo(
    () => [{ scope: "Organization", branchName: "", data }],
    [data]
  );

  const saveRenderConfig = () => {
    dispatch(
      saveReportRenderSetting({
        userId: userData.id,
        organizationId,
        reportTypeId,
        renderConfig: effectiveRenderConfig,
      })
    );
  };

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} Organization Tickets Per Service</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {loading || isLoadingRenderSetting ? <Loader /> : !_.isEmpty(data) ? (
        <>
          <Box>
            <HStack flexDir="row" justifyContent="space-between">
              <Heading fontSize="34px" color="#222" fontWeight="700">
                Tickets Per Service
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      {getReportDescription(reportRange).TicketsPerService}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              </Heading>

              <DownloadReport
                reportRange={reportRange}
                reportTypeId={reportTypeId}
                renderConfig={effectiveRenderConfig}
                onSave={saveRenderConfig}
                isSaving={savingReportRenderSetting}
                onDownloadCsv={() =>
                  downloadTicketsPerServiceCsvFiles({
                    scopes: csvScopes,
                    renderConfig: effectiveRenderConfig,
                    filenamePrefix: "organization_tickets_per_service",
                  })
                }
              />
            </HStack>
          </Box>

          <ReportPresentationControls
            renderConfig={effectiveRenderConfig}
            onChange={setRenderConfig}
            columnDefinitions={TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
            defaultSortColumn="total"
          />
              <ReportPresentationSummary items={presentationSummaryItems} />

          <TicketsPerService
            data={data}
            renderConfig={effectiveRenderConfig}
            onRenderConfigChange={setRenderConfig}
          />
        </>
      ) : (
        <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
      )}
    </Box>
  );
};

export default TicketsPerServicePage;
